import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FiAward, FiGlobe, FiBookOpen } from 'react-icons/fi';
import ScrollGrid from './ScrollGrid';
import FlickerText from './FlickerText';
import './Education.css';

const educationData = [
  {
    institution: 'Polytechnic University of the Philippines',
    campus: 'Santa Maria, Bulacan Campus',
    degree: 'Bachelor of Science in Information Technology',
    period: '2022 - PRESENT',
    color: '#7c6aef',
    coursework: ['Web Development', 'Data Structures', 'HCI', 'Software Engineering', 'Database Systems'],
  },
];

const certifications = [
  {
    title: 'Salesforce Supported Virtual Internship Program 2025',
    issuer: 'SmartBridge - Philippines',
    year: '2025',
    color: '#06b6d4',
  },
  {
    title: 'Capstone to Startup: Preparing Students for Innovation Challenges',
    issuer: 'DICT - Philippines',
    year: '2025',
    color: '#120ef8ff',
  },
  {
    title: 'Introduction to Cybersecurity',
    issuer: 'Cisco Networking Academy',
    year: '2024',
    color: '#9b6aef',
  },
  {
    title: 'Introduction to Packet Tracer',
    issuer: 'Cisco Networking Academy',
    year: '2023',
    color: '#6e1affff',
  },
 
];

const languages = [
  { name: 'Tagalog', level: 'Native', bar: 100 },
  { name: 'English', level: 'Proficient', bar: 85 },
];

const Education = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.15 });

  return (
    <section className="education" id="education" ref={ref}>
      <ScrollGrid fadeVariant="fade-top-right" />
      <div className="container">
        <motion.div
          className="education-header"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-label">Background</span>
          <h2 className="section-big-title">
            <FlickerText text="HOW IT" isInView={isInView} /><br />
            <span className="outline"><FlickerText text="STARTED" isInView={isInView} delay={0.3} /></span><span className="muted">?</span>
          </h2>
        </motion.div>

        <div className="education-layout">
          <div className="edu-main-col">
            {educationData.map((edu, index) => (
              <motion.div
                className="edu-main-card glass"
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="edu-main-top">
                  <div className="edu-main-badge" style={{ color: edu.color }}><FiBookOpen /></div>
                  <span className="edu-main-period" style={{ color: edu.color }}>{edu.period}</span>
                </div>
                <h3 className="edu-main-title">{edu.institution}</h3>
                <p className="edu-main-campus">{edu.campus}</p>
                <p className="edu-main-degree">{edu.degree}</p>
                <div className="edu-coursework">
                  <span className="coursework-label">Relevant Coursework</span>
                  <div className="coursework-tags">
                    {edu.coursework.map((c, i) => (
                      <span className="tag" key={i}>{c}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}

            <motion.div
              className="lang-card glass"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <div className="lang-header">
                <FiGlobe />
                <span>Languages</span>
              </div>
              <div className="lang-list">
                {languages.map((lang, i) => (
                  <div className="lang-item" key={i}>
                    <div className="lang-info">
                      <span className="lang-name">{lang.name}</span>
                      <span className="lang-level">{lang.level}</span>
                    </div>
                    <div className="lang-bar">
                      <motion.div
                        className="lang-bar-fill"
                        initial={{ width: 0 }}
                        animate={isInView ? { width: `${lang.bar}%` } : { width: 0 }}
                        transition={{ duration: 1, delay: 0.7 + i * 0.15 }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="edu-side-col">
            <motion.div
              className="cert-section-label"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <FiAward />
              <span>Certifications</span>
            </motion.div>

            {certifications.map((cert, index) => (
              <motion.div
                className="cert-card glass"
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.15 }}
                whileHover={{ y: -4, borderColor: `${cert.color}40` }}
              >
                <div className="cert-dot" style={{ background: cert.color }} />
                <div className="cert-body">
                  <h4>{cert.title}</h4>
                  <div className="cert-meta">
                    <span className="cert-issuer">{cert.issuer}</span>
                    <span className="cert-year" style={{ color: cert.color }}>{cert.year}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;
